import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import InventoryShell from "./InventoryShell";
import {
  AlertTriangle, ArrowLeft, Check, Clock, Trash2, RotateCcw, Ban,
  ChevronDown, ChevronUp, StickyNote,
} from "lucide-react";

/**
 * Variance Review + Close-out — Phase I3.
 *
 * Reached from RetailAudit when the session finalizes (in_progress → variance_review).
 * Lists every variance sorted by |value| desc, offers four resolutions per row
 * (Accept · Recount · Write-off · Defer), plus one bulk "Accept Minor Variances"
 * action (fixed $5 threshold). Close Audit becomes available once all variances
 * are resolved.
 */

const RESOLUTION_STYLES = {
  accepted:    { label: "Accepted",     dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-800" },
  recounted:   { label: "Recounted",    dot: "bg-sky-500",     pill: "bg-sky-50 text-sky-800" },
  written_off: { label: "Written off",  dot: "bg-rose-500",    pill: "bg-rose-50 text-rose-800" },
  deferred:    { label: "Deferred",     dot: "bg-stone-400",   pill: "bg-stone-100 text-stone-700" },
};

function money(n) {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.abs(v).toFixed(2)}`;
}

function VarianceRow({ variance, onResolve, canEdit }) {
  const [open, setOpen] = useState(false);
  const [pendingRes, setPendingRes] = useState(null);
  const [explanation, setExplanation] = useState(variance.explanation || "");
  const [recountQty, setRecountQty] = useState(variance.counted ?? 0);
  const [saving, setSaving] = useState(false);

  const isResolved = !!variance.resolution;
  const style = variance.resolution ? RESOLUTION_STYLES[variance.resolution] : null;
  const value = Number(variance.variance_value_usd || 0);
  const valueMagnitudePositive = value >= 0;

  const commit = async (resolution) => {
    if (resolution === "written_off" || resolution === "deferred") {
      if (!explanation.trim()) return;
    }
    setSaving(true);
    try {
      const body = { resolution, explanation: explanation.trim() || undefined };
      if (resolution === "recounted") body.counted_qty = Number(recountQty);
      await onResolve(variance.id, body);
      setPendingRes(null);
      setOpen(false);
    } finally { setSaving(false); }
  };

  return (
    <li className={`bg-white subtle-border rounded-2xl transition-shadow ${open ? "shadow-sm" : ""}`} data-testid={`variance-row-${variance.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-4 p-4 sm:p-5 text-left"
        aria-expanded={open}
        data-testid={`variance-toggle-${variance.id}`}
      >
        <div className={`w-1 self-stretch rounded-full ${valueMagnitudePositive ? "bg-sky-300" : "bg-amber-300"}`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-stone-900 font-medium">{variance.product_display_name}</div>
          <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>expected {variance.expected}</span>
            <span>·</span>
            <span>counted {variance.counted}</span>
            <span>·</span>
            <span>{variance.variance_qty > 0 ? "+" : ""}{variance.variance_qty} units</span>
            {variance.toto_suggested_cause && (
              <>
                <span>·</span>
                <span className="italic text-stone-400">{variance.toto_suggested_cause}</span>
              </>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`text-sm font-medium ${value >= 0 ? "text-sky-700" : "text-amber-700"}`}>
            {money(value)}
          </div>
          {isResolved ? (
            <div className={`mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${style.pill}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
              {style.label}
            </div>
          ) : (
            <div className="mt-1 text-[10px] text-stone-400 uppercase tracking-wider">Unresolved</div>
          )}
        </div>
        {open ? <ChevronUp size={14} className="text-stone-400 shrink-0" /> : <ChevronDown size={14} className="text-stone-400 shrink-0" />}
      </button>

      {open && (
        <div className="px-4 sm:px-5 pb-5 border-t border-stone-100 space-y-3" data-testid={`variance-detail-${variance.id}`}>
          {variance.notes_at_count && (
            <div className="mt-3 flex items-start gap-2 text-xs text-stone-600 bg-stone-50 rounded-lg px-3 py-2">
              <StickyNote size={12} className="mt-0.5 shrink-0 text-stone-400" />
              <span>{variance.notes_at_count}</span>
            </div>
          )}
          {variance.explanation && isResolved && (
            <div className="mt-3 text-xs text-stone-600">
              <span className="text-stone-400">Resolution note:</span> {variance.explanation}
              {variance.resolved_by_name && (
                <span className="text-stone-400"> — by {variance.resolved_by_name}</span>
              )}
            </div>
          )}

          {canEdit && !isResolved && (
            <>
              {!pendingRes && (
                <div className="flex flex-wrap gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => commit("accepted")}
                    disabled={saving}
                    data-testid={`variance-accept-${variance.id}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5"
                  >
                    <Check size={12} /> Accept variance
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingRes("recounted")}
                    data-testid={`variance-recount-open-${variance.id}`}
                    className="text-xs px-3 py-1.5 rounded-full subtle-border hover:bg-stone-50 inline-flex items-center gap-1.5"
                  >
                    <RotateCcw size={12} /> Recount
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingRes("written_off")}
                    data-testid={`variance-writeoff-open-${variance.id}`}
                    className="text-xs px-3 py-1.5 rounded-full subtle-border hover:bg-stone-50 inline-flex items-center gap-1.5"
                  >
                    <Trash2 size={12} /> Write off
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingRes("deferred")}
                    data-testid={`variance-defer-open-${variance.id}`}
                    className="text-xs px-3 py-1.5 rounded-full subtle-border hover:bg-stone-50 inline-flex items-center gap-1.5"
                  >
                    <Ban size={12} /> Defer
                  </button>
                </div>
              )}

              {pendingRes === "recounted" && (
                <div className="mt-3 rounded-2xl bg-sky-50 p-4 space-y-2" data-testid={`variance-recount-form-${variance.id}`}>
                  <div className="text-[10px] label-eyebrow text-sky-700">Recount</div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-stone-700">New count</label>
                    <input
                      type="number"
                      min={0}
                      value={recountQty}
                      onChange={(e) => setRecountQty(e.target.value)}
                      data-testid={`variance-recount-qty-${variance.id}`}
                      className="w-24 px-3 py-1.5 rounded-lg subtle-border text-sm outline-none"
                    />
                    <span className="text-[11px] text-stone-500">expected was {variance.expected}</span>
                  </div>
                  <textarea
                    value={explanation}
                    onChange={(e) => setExplanation(e.target.value)}
                    rows={2}
                    placeholder="Optional note about the recount…"
                    className="w-full text-sm px-3 py-2 rounded-lg subtle-border outline-none bg-white"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => commit("recounted")}
                      disabled={saving}
                      data-testid={`variance-recount-confirm-${variance.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40"
                    >
                      Confirm recount
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingRes(null)}
                      className="text-xs px-3 py-1.5 rounded-full subtle-border hover:bg-stone-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {(pendingRes === "written_off" || pendingRes === "deferred") && (
                <div className={`mt-3 rounded-2xl p-4 space-y-2 ${pendingRes === "written_off" ? "bg-rose-50" : "bg-stone-50"}`}
                  data-testid={`variance-${pendingRes}-form-${variance.id}`}>
                  <div className="text-[10px] label-eyebrow text-stone-600">
                    {pendingRes === "written_off" ? "Write-off — explanation required" : "Defer — explanation required"}
                  </div>
                  <textarea
                    value={explanation}
                    onChange={(e) => setExplanation(e.target.value)}
                    rows={3}
                    autoFocus
                    placeholder={pendingRes === "written_off" ? "What accounts for this write-off?" : "Why defer this — what needs to happen first?"}
                    className="w-full text-sm px-3 py-2 rounded-lg subtle-border outline-none bg-white"
                    data-testid={`variance-${pendingRes}-note-${variance.id}`}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => commit(pendingRes)}
                      disabled={saving || !explanation.trim()}
                      data-testid={`variance-${pendingRes}-confirm-${variance.id}`}
                      className={`text-xs px-3 py-1.5 rounded-full text-white disabled:opacity-40 ${pendingRes === "written_off" ? "bg-rose-600 hover:bg-rose-700" : "bg-stone-800 hover:bg-stone-900"}`}
                    >
                      Confirm {pendingRes === "written_off" ? "write-off" : "defer"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingRes(null)}
                      className="text-xs px-3 py-1.5 rounded-full subtle-border hover:bg-stone-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

export default function VarianceReview() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ session: null, variances: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const refresh = async () => {
    const r = await api.get(`/inventory/audit/sessions/${sessionId}/variances`);
    setState(r.data);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        await refresh();
      } catch (e) {
        if (alive) setErrorMsg(e?.response?.data?.detail || "Could not load variance review.");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const resolve = async (varianceId, body) => {
    await api.patch(`/inventory/variances/${varianceId}`, body);
    await refresh();
  };

  const bulkAcceptMinor = async () => {
    await api.post(`/inventory/audit/sessions/${sessionId}/variances/accept-minor`, { dry_run: false });
    await refresh();
  };

  const closeAudit = async () => {
    setClosing(true);
    setErrorMsg("");
    try {
      const r = await api.post(`/inventory/audit/sessions/${sessionId}/close`, {});
      navigate(`/operations/inventory?closed=${r.data.session.id}`);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Could not close audit.");
    } finally { setClosing(false); }
  };

  const stats = state.stats || {};
  const session = state.session;
  const variances = state.variances || [];
  const allResolved = variances.length > 0 && variances.every((v) => v.resolution);
  const isClosed = session?.status === "closed";
  const canEdit = session?.status === "variance_review";

  const minorUnresolved = stats.minor_unresolved || 0;
  const threshold = stats.minor_threshold_usd ?? 5;

  if (loading) {
    return <InventoryShell title="Variance Review"><div className="text-sm text-stone-400">Loading…</div></InventoryShell>;
  }

  if (!session) {
    return (
      <InventoryShell title="Variance Review">
        <div className="text-sm text-stone-500">{errorMsg || "Session not found."}</div>
      </InventoryShell>
    );
  }

  return (
    <InventoryShell
      title="Variance Review"
      right={
        <button
          onClick={() => navigate(`/operations/inventory/audit/${sessionId}`)}
          className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"
          data-testid="variance-back-to-audit"
        >
          <ArrowLeft size={12} /> Back to counts
        </button>
      }
    >
      {/* Header strip */}
      <div className="bg-white subtle-border rounded-3xl p-5 sm:p-6 mb-5" data-testid="variance-header">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] label-eyebrow text-stone-400">{session.boutique_code} · {session.kind} audit</div>
            <h2 className="font-serif-display text-stone-900 text-2xl mt-1">Review the discrepancies</h2>
            <p className="text-xs text-stone-500 mt-1 max-w-lg">
              Highest-impact variances first. Every variance needs a resolution before you can close the audit.
            </p>
          </div>
          {isClosed && (
            <div className="text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 inline-flex items-center gap-1.5" data-testid="variance-closed-pill">
              <Check size={12} /> Audit closed
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <Stat label="Variances" value={stats.total ?? 0} testid="variance-stat-total" />
          <Stat label="Unresolved" value={stats.unresolved ?? 0} tone={(stats.unresolved || 0) > 0 ? "amber" : "calm"} testid="variance-stat-unresolved" />
          <Stat label="Minor unresolved" hint={`under $${threshold}`} value={minorUnresolved} testid="variance-stat-minor" />
          <Stat label="Total impact" value={money(stats.total_value_usd || 0)} testid="variance-stat-value" />
        </div>
      </div>

      {/* Bulk + close row */}
      {canEdit && variances.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <button
            type="button"
            onClick={bulkAcceptMinor}
            disabled={minorUnresolved === 0}
            data-testid="variance-bulk-accept-minor"
            className="text-sm px-4 py-2 rounded-full subtle-border bg-white hover:bg-stone-50 disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Check size={13} strokeWidth={1.5} />
            Accept Minor Variances{minorUnresolved > 0 ? ` (${minorUnresolved})` : ""}
            <span className="text-[10px] text-stone-400">under ${threshold}</span>
          </button>

          <div className="flex-1" />

          <button
            type="button"
            onClick={closeAudit}
            disabled={!allResolved || closing}
            data-testid="variance-close-audit"
            className="text-sm px-5 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40 inline-flex items-center gap-2"
          >
            {closing ? "Closing…" : "Close audit"}
          </button>
        </div>
      )}

      {errorMsg && (
        <div className="rounded-xl bg-rose-50 text-rose-800 text-sm px-4 py-3 mb-4 flex items-start gap-2" data-testid="variance-error">
          <AlertTriangle size={14} className="mt-0.5" />
          <span>{errorMsg}</span>
        </div>
      )}

      {variances.length === 0 ? (
        <div className="bg-white subtle-border rounded-3xl p-10 text-sm text-stone-500 text-center" data-testid="variance-empty">
          Every count matched. Nothing to review — ready to close whenever you are.
          {canEdit && (
            <div className="mt-4">
              <button
                type="button"
                onClick={closeAudit}
                disabled={closing}
                data-testid="variance-close-audit-empty"
                className="text-sm px-5 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40"
              >
                {closing ? "Closing…" : "Close audit"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <ul className="space-y-3" data-testid="variance-list">
          {variances.map((v) => (
            <VarianceRow key={v.id} variance={v} onResolve={resolve} canEdit={canEdit} />
          ))}
        </ul>
      )}
    </InventoryShell>
  );
}

function Stat({ label, value, hint, tone = "calm", testid }) {
  const toneCls = tone === "amber" ? "text-amber-700" : "text-stone-900";
  return (
    <div className="rounded-2xl bg-stone-50/60 subtle-border px-4 py-3" data-testid={testid}>
      <div className="text-[10px] label-eyebrow text-stone-400">{label}</div>
      <div className={`mt-1 font-serif-display text-2xl ${toneCls}`}>{value}</div>
      {hint && <div className="text-[10px] text-stone-400 mt-0.5">{hint}</div>}
    </div>
  );
}
